import { updateRequiredCompetences } from "@/api/Activities";
import { Trans } from "@lingui/react/macro";
import { FilledButton } from "@medoma/components/Button/Button";
import Checkbox from "@/components/Checkbox/Checkbox";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Form from "@/components/Form/Form";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import {
  medicalCompetenceDictionary,
  medicalCompetenceSchema,
} from "@medoma/models/shifts";
import { useLingui } from "@lingui/react/macro";
import { useEnabledCompetences } from "@/api/Shifts";

export const EditRequiredCompetences = ({
  currentRequiredCompetences,
  isRequired,
  onSuccess,
  activityId,
}: {
  currentRequiredCompetences: IMedicalCompetence[];
  isRequired: boolean;
  onSuccess: () => void;
  activityId: string;
}) => {
  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      requiredCompetences: currentRequiredCompetences,
    },
  });

  const queryClient = useQueryClient();
  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (requiredCompetences: IMedicalCompetence[]) =>
      updateRequiredCompetences(activityId, requiredCompetences),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  const { t } = useLingui();

  const {
    data: enabledCompetences,
    isPending: isPendingEnabledCompetences,
    isError: isErrorEnabledCompetences,
  } = useEnabledCompetences();

  return (
    <Form
      onSubmit={handleSubmit((formData) => {
        mutate(formData.requiredCompetences);
      })}
    >
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar kompetensbehov`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <Form.Row>
            <Checkboxes errorMessage={errors.requiredCompetences?.message}>
              {isPendingEnabledCompetences ? (
                <Loading message={t`Hämtar lista med kompetenser`} />
              ) : isErrorEnabledCompetences ? (
                <ErrorMessage
                  message={t`Kunde inte hämta lista med kompetenser`}
                />
              ) : (
                enabledCompetences.map((competence) => (
                  <Checkbox
                    key={competence}
                    label={{
                      text: t(medicalCompetenceDictionary[competence].long),
                    }}
                    {...register(`requiredCompetences`, {
                      required: {
                        value: isRequired,
                        message: t`Ange minst en kompetens`,
                      },
                    })}
                    value={medicalCompetenceSchema.enum[competence]}
                  />
                ))
              )}
            </Checkboxes>
          </Form.Row>
          {isDirty ? (
            <FilledButton type="submit">
              <Trans>Spara ändringar</Trans>
            </FilledButton>
          ) : (
            <></>
          )}
        </>
      )}
    </Form>
  );
};
